import React from 'react';
import {Link} from 'react-router-dom';
import {wordmarkUrl} from '@acuwize/shared';
import './marketing.scss';

// Public-facing landing page. Replaces <Login> as the / branch for
// unauthenticated users (the auth-aware shell in App.js dispatches here
// when !user). Faithful conversion of tenetai-marketing-v1.html with the
// Tailwind-CDN dependency dropped — utility classes hand-translated to
// BEM-style SCSS in marketing.scss using shared/tokens.scss.
//
// Sign-in path: header CTA → /login (the Login page with the three OIDC
// provider buttons; lives separately so this page stays sales-shaped, not
// dashboard-shaped).
//
// All anchor hrefs (#why, #how, etc.) target section ids on this page;
// internal links use react-router-dom <Link> only when navigating across
// routes (just /login here).

const Check = () => (
    <svg className="marketing-icon-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
        <path d="M5 13l4 4L19 7" />
    </svg>
);

const Chevron = () => (
    <svg className="marketing-icon-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M6 9l6 6 6-6" />
    </svg>
);

function Marketing() {
    return (
        <div className="marketing">
            <header className="marketing-nav">
                <div className="marketing-nav__inner">
                    <a href="/" className="marketing-nav__brand">
                        <img src={wordmarkUrl} alt="Acuwize" className="marketing-nav__wordmark" />
                        <span className="marketing-nav__sep" aria-hidden="true">/</span>
                        <span className="marketing-nav__product">Tenet</span>
                    </a>
                    <nav className="marketing-nav__links">
                        <a href="#why">Why Tenet</a>
                        <a href="#how">How it works</a>
                        <a href="#capabilities">Capabilities</a>
                        <a href="#scale">Scale</a>
                        <a href="#faq">FAQ</a>
                    </nav>
                    <div className="marketing-nav__cta">
                        <Link to="/login" className="marketing-btn marketing-btn--ghost">Sign in</Link>
                        <a href="#contact" className="marketing-btn marketing-btn--primary">Talk to us</a>
                    </div>
                </div>
            </header>

            <section className="marketing-hero">
                <div className="marketing-hero__inner">
                    <div className="marketing-hero__pitch">
                        <span className="marketing-pill">For industry standards consortia</span>
                        <h1 className="marketing-hero__title">
                            Multi-party API contracts.{' '}
                            <span className="marketing-hero__accent">Negotiated. Ratified. Published.</span>
                        </h1>
                        <p className="marketing-hero__lede">
                            The documentation and the API stop being two artifacts that drift.
                            Domain experts author contracts in a visual editor and property forms;
                            Tenet writes a single OpenAPI 3.1 file behind the scenes —{' '}
                            <strong>models, APIs, webhooks</strong>, and the rich documentation that
                            explains them, all in one canonical source of truth, fully versioned in
                            your GitHub repository.
                        </p>
                        <div className="marketing-hero__ctas">
                            <Link to="/login" className="marketing-btn marketing-btn--primary">Start free</Link>
                            <a href="#contact" className="marketing-btn marketing-btn--secondary">Book a 30-minute demo</a>
                        </div>
                        <ul className="marketing-hero__bullets">
                            <li><Check /> Production-scale (1,000+ endpoints)</li>
                            <li><Check /> OpenAPI 3.0 / 3.1 + AsyncAPI</li>
                            <li><Check /> Git-backed, GitHub native</li>
                        </ul>
                    </div>

                    <div className="marketing-hero__mockup">
                        <div className="marketing-hero__mockup-frame">
                            <div className="marketing-mockup">
                                <div className="marketing-mockup__chrome">
                                    <span className="marketing-mockup__dot" />
                                    <span className="marketing-mockup__dot" />
                                    <span className="marketing-mockup__dot" />
                                    <span className="marketing-mockup__url">tenet.acuwize.cloud / openlogistics / Location</span>
                                </div>
                                <div className="marketing-mockup__pickers">
                                    <span className="marketing-mockup__picker">OpenLogistics <Chevron /></span>
                                    <span className="marketing-mockup__picker marketing-mockup__picker--right">v2019.06.20 (workspace) <Chevron /></span>
                                </div>
                                <div className="marketing-mockup__tabs">
                                    <span>Overview</span>
                                    <span className="marketing-mockup__tab--active">
                                        Models <span className="marketing-mockup__count">(592)</span>
                                    </span>
                                    <span>APIs <span className="marketing-mockup__count-muted">(1168)</span></span>
                                    <span>Webhooks <span className="marketing-mockup__count-muted">(2)</span></span>
                                </div>
                                <div className="marketing-mockup__panes">
                                    <div className="marketing-mockup__tree">
                                        <div className="marketing-mockup__tree-label">SCHEMAS (592)</div>
                                        <div>amountType</div>
                                        <div>▾ bills</div>
                                        <div className="marketing-mockup__tree-indent-1">▾ lineItems</div>
                                        <div className="marketing-mockup__tree-indent-2">costRefs</div>
                                        <div className="marketing-mockup__tree-indent-2">refnums</div>
                                        <div className="marketing-mockup__tree-active">Location</div>
                                        <div>orderReleases</div>
                                        <div>ports</div>
                                    </div>
                                    <div className="marketing-mockup__detail">
                                        <div className="marketing-mockup__detail-name">Location</div>
                                        <div className="marketing-mockup__field">
                                            <span className="marketing-mockup__field-name">domainName</span>
                                            <span className="marketing-mockup__field-type"> : string</span>
                                            <span className="marketing-mockup__tag marketing-mockup__tag--required">required</span>
                                        </div>
                                        <div className="marketing-mockup__field">
                                            <span className="marketing-mockup__field-name">parkingName</span>
                                            <span className="marketing-mockup__field-type"> : string</span>
                                        </div>
                                        <div className="marketing-mockup__field">
                                            <span className="marketing-mockup__field-name">weights</span>
                                            <span className="marketing-mockup__field-type"> : array of </span>
                                            <span className="marketing-mockup__ref">$ref → common/WeightMeasure</span>
                                        </div>
                                        <div className="marketing-mockup__field">
                                            <span className="marketing-mockup__field-name">insertDate</span>
                                            <span className="marketing-mockup__field-type"> : object</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <aside className="marketing-refdialog">
                                <header className="marketing-refdialog__head">
                                    <span>Add Schema $ref</span>
                                    <span className="marketing-refdialog__close">×</span>
                                </header>
                                <div className="marketing-refdialog__body">
                                    <div className="marketing-refdialog__row">
                                        <div className="marketing-refdialog__label">SCHEMA REFERENCE *</div>
                                        <div className="marketing-refdialog__select">Common <Chevron /></div>
                                    </div>
                                    <div className="marketing-refdialog__row">
                                        <div className="marketing-refdialog__label">TARGET MODEL *</div>
                                        <div className="marketing-refdialog__select">
                                            <span className="marketing-refdialog__mono">WeightMeasure</span>
                                            <Chevron />
                                        </div>
                                    </div>
                                    <div className="marketing-refdialog__split">
                                        <div>
                                            <div className="marketing-refdialog__label">TYPE</div>
                                            <div className="marketing-refdialog__radios">
                                                <label><span className="marketing-refdialog__radio" />Object</label>
                                                <label><span className="marketing-refdialog__radio marketing-refdialog__radio--checked" />Array</label>
                                            </div>
                                        </div>
                                        <div className="marketing-refdialog__field-name">
                                            <div className="marketing-refdialog__label">FIELD NAME</div>
                                            <div className="marketing-refdialog__field-input">weights</div>
                                        </div>
                                    </div>
                                    <div className="marketing-refdialog__actions">
                                        <button type="button" className="marketing-refdialog__btn">Cancel</button>
                                        <button type="button" className="marketing-refdialog__btn marketing-refdialog__btn--primary">Add $ref</button>
                                    </div>
                                </div>
                            </aside>

                            <div className="marketing-hero__caption">One canvas. Models, APIs, Webhooks.</div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="why" className="marketing-section marketing-section--tinted">
                <div className="marketing-section__inner marketing-section__inner--narrow marketing-section__inner--center">
                    <span className="marketing-pill">Why now</span>
                    <h2 className="marketing-section__title">
                        Every team can vibe-code an OpenAPI spec in an afternoon.<br />
                        <span className="marketing-section__title-accent">Standards bodies have to adjudicate between hundreds of them.</span>
                    </h2>
                    <p className="marketing-section__lede">
                        Authoring a contract just got cheap. Negotiating, reviewing, ratifying, and publishing the canonical
                        version that 14 member companies will code against — that&rsquo;s the bottleneck. Acuwize Tenet is the
                        curation layer the LLM era creates demand for.
                    </p>
                </div>
            </section>

            <section className="marketing-section">
                <div className="marketing-section__inner">
                    <header className="marketing-section__header">
                        <span className="marketing-pill">What makes it different</span>
                        <h2 className="marketing-section__title">Three things SwaggerHub, Stoplight, and Postman don&rsquo;t do.</h2>
                        <p className="marketing-section__lede">
                            Generic API tools are designed for one product team shipping one API. Standards bodies are running
                            federated, cross-organisation governance. Different problem, different tool.
                        </p>
                    </header>

                    <div className="marketing-diff">
                        <article className="marketing-diff__card">
                            <div className="marketing-diff__icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                    <path d="M10 13a5 5 0 007 0l3-3a5 5 0 00-7-7l-1 1" />
                                    <path d="M14 11a5 5 0 00-7 0l-3 3a5 5 0 007 7l1-1" />
                                </svg>
                            </div>
                            <h3>Federated cross-workgroup <span className="marketing-mono marketing-text-accent">$ref</span></h3>
                            <p>A schema in your <em>logistics</em> workgroup references a type in the <em>foundation</em> workgroup,
                                even though they live in different repos managed by different teams. The editor resolves the path,
                                follows the link, and warns you when the source changes.</p>
                            <pre className="marketing-snippet">
weights: array of <span className="marketing-text-accent">$ref → common/WeightMeasure</span>
                            </pre>
                        </article>

                        <article className="marketing-diff__card">
                            <div className="marketing-diff__icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                    <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M22 21v-2a4 4 0 00-3-3.87" />
                                    <path d="M16 3.13a4 4 0 010 7.75" />
                                </svg>
                            </div>
                            <h3>Authoring for the people who actually own the standard</h3>
                            <p>Logistics planners, treasury leads, FHIR analysts. Domain experts who understand the schema
                                semantically but can&rsquo;t write YAML. They fill in a form. Acuwize Tenet generates the spec.</p>
                            <div className="marketing-diff__form">
                                <div className="marketing-diff__form-cell">Name<div className="marketing-mono">orderRef</div></div>
                                <div className="marketing-diff__form-cell">Type<div className="marketing-mono">string</div></div>
                                <div className="marketing-diff__form-cell marketing-diff__form-cell--wide">
                                    Flags
                                    <div className="marketing-diff__flags">
                                        <span className="marketing-diff__flag marketing-diff__flag--accent">Required</span>
                                        <span className="marketing-diff__flag">Read-Only</span>
                                    </div>
                                </div>
                            </div>
                        </article>

                        <article className="marketing-diff__card">
                            <div className="marketing-diff__icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                                </svg>
                            </div>
                            <h3>REST endpoints and event types in one editor</h3>
                            <p>A webhook for <span className="marketing-mono">orderStatusChanged</span> can <span className="marketing-mono">$ref</span> the
                                message payload from your AsyncAPI document. Same workspace, same shared schemas. SwaggerHub treats
                                these as different products. We don&rsquo;t.</p>
                            <pre className="marketing-snippet">
<span className="marketing-text-accent">webhook:</span> orderStatusChanged{'\n'}
{'  '}<span className="marketing-text-accent">$ref:</span> ./full-asyncapi.yaml{'\n'}
{'    '}#/components/messages/{'\n'}
{'    '}OrderStatusChangedEvent
                            </pre>
                        </article>
                    </div>
                </div>
            </section>

            <section id="how" className="marketing-section marketing-section--tinted">
                <div className="marketing-section__inner">
                    <header className="marketing-section__header">
                        <span className="marketing-pill">How it works</span>
                        <h2 className="marketing-section__title">A workflow that mirrors how consortia actually work.</h2>
                        <p className="marketing-section__lede">
                            Every editor gets a private workspace. Pull updates from your workgroup. Submit changes for review.
                            The admin publishes the canonical version to GitHub for member companies to consume.
                        </p>
                    </header>

                    <div className="marketing-how">
                        {[
                            {n: 1, h: 'Author in your workspace', p: 'Open a spec, edit models, APIs, and webhooks in a visual form. Your changes live in a private git-backed sandbox until you submit.'},
                            {n: 2, h: 'Pull from teammates',      p: 'When the foundation workgroup updates a shared type, you pull the change into your workspace. See the impact before publish.'},
                            {n: 3, h: 'Submit for review',         p: 'Editors vote, comment on schema fields, and merge approved changes. Subscribers can comment and upvote without editing rights.'},
                            {n: 4, h: 'Publish the canonical',     p: 'Admin tags a release. The canonical OpenAPI YAML and Markdown documentation go to GitHub. Member companies code against the published version.'},
                        ].map((step) => (
                            <article key={step.n} className="marketing-how__card">
                                <div className="marketing-how__num">{step.n}</div>
                                <h3>{step.h}</h3>
                                <p>{step.p}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section id="capabilities" className="marketing-section">
                <div className="marketing-section__inner">
                    <header className="marketing-section__header">
                        <span className="marketing-pill">Capabilities</span>
                        <h2 className="marketing-section__title">Everything a standards body needs. Nothing it doesn&rsquo;t.</h2>
                    </header>

                    <div className="marketing-caps">
                        {[
                            {h: 'Visual schema editor',                                          p: 'Form-based property editor with type, flags, examples, external docs. Toggle to raw YAML or JSON via Monaco for power users.'},
                            {h: <>Cross-workgroup <span className="marketing-mono">$ref</span> wizard</>, p: 'Pick a source workgroup, pick a model, choose Object or Array. The path, file, and pointer are generated for you.'},
                            {h: 'Per-user git workspaces',                                       p: 'Every editor works in their own sandbox. Pull updates from teammates. Submit when ready. Conflicts visible early.'},
                            {h: 'MDX-rich descriptions',                                          p: 'Field descriptions support headings, lists, tables, links, and code blocks — for the governance context standard bodies actually need.'},
                            {h: 'Auto-generated samples',                                         p: 'Request and response JSON examples generated from the schema, on every endpoint. Keeps documentation honest.'},
                            {h: 'Built-in linting',                                                p: 'Style guide enforcement and breaking-change detection on every save. SwaggerHub charges extra for this; it ships in the box.'},
                            {h: 'Subscriber comment + vote',                                       p: 'Read-only members can comment on fields, upvote change proposals, and feed input back to editors without editing rights.'},
                            {h: 'Webhooks & events',                                              p: <>REST endpoints and webhook events live in the same editor. Cross-format <span className="marketing-mono">$ref</span> between OpenAPI and AsyncAPI documents.</>},
                            {h: 'SSO via Google & Microsoft',                                     p: 'Member companies log in with the identity they already have. SAML and Okta connectors available for enterprise tiers.'},
                        ].map((cap, i) => (
                            <div key={i} className="marketing-caps__cell">
                                <h3>{cap.h}</h3>
                                <p>{cap.p}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section id="scale" className="marketing-section marketing-section--tinted">
                <div className="marketing-section__inner marketing-scale">
                    <div className="marketing-scale__copy">
                        <span className="marketing-pill">Built for production-scale specs</span>
                        <h2 className="marketing-section__title">If it loads in our editor, it can hold your standard.</h2>
                        <p className="marketing-section__lede">
                            Acuwize Tenet runs the full <strong>Oracle Transportation Management</strong> public API — 1,168 endpoints,
                            592 schemas — without breaking a sweat. We&rsquo;ve also tested it on <strong>Stripe</strong>,{' '}
                            <strong>Adyen 3.1</strong>, and reference supply-chain specs. Most editors choke around 200 endpoints.
                            We start there.
                        </p>
                        <div className="marketing-scale__stats">
                            <div className="marketing-scale__stat"><div className="marketing-scale__stat-num">1,168</div><div>endpoints (OTM)</div></div>
                            <div className="marketing-scale__stat"><div className="marketing-scale__stat-num">592</div><div>schemas (OTM)</div></div>
                            <div className="marketing-scale__stat"><div className="marketing-scale__stat-num marketing-text-accent">14<span className="marketing-scale__stat-suffix">+</span></div><div>months in production at BiTA Standards</div></div>
                            <div className="marketing-scale__stat"><div className="marketing-scale__stat-num marketing-text-accent">0</div><div>YAML required from non-developers</div></div>
                        </div>
                    </div>

                    <div className="marketing-scale__mockup">
                        <div className="marketing-mockup marketing-mockup--lg">
                            <div className="marketing-mockup__chrome">
                                <span className="marketing-mockup__picker">OpenLogistics</span>
                                <span className="marketing-mockup__url marketing-mockup__url--right">v2019.06.20 (workspace)</span>
                            </div>
                            <div className="marketing-mockup__tabs marketing-mockup__tabs--lg">
                                <span>Overview</span>
                                <span className="marketing-mockup__tab--active">Models <span className="marketing-mockup__count">(592)</span></span>
                                <span>APIs <span className="marketing-mockup__count-muted">(1168)</span></span>
                                <span>Webhooks <span className="marketing-mockup__count-muted">(2)</span></span>
                            </div>
                            <div className="marketing-mockup__panes">
                                <div className="marketing-mockup__tree">
                                    <div className="marketing-mockup__tree-label">SCHEMAS (592)</div>
                                    <div>amountType</div>
                                    <div>▸ appointments</div>
                                    <div>▾ bills</div>
                                    <div className="marketing-mockup__tree-indent-1">▸ equipmentDetails</div>
                                    <div className="marketing-mockup__tree-indent-1">involvedParties</div>
                                    <div className="marketing-mockup__tree-indent-1">▾ lineItems</div>
                                    <div className="marketing-mockup__tree-indent-2">commercialData</div>
                                    <div className="marketing-mockup__tree-indent-2 marketing-mockup__tree-active">costRefs</div>
                                    <div className="marketing-mockup__tree-indent-2">refnums</div>
                                    <div className="marketing-mockup__tree-indent-2">vouchers</div>
                                    <div>orderReleases</div>
                                    <div>ports</div>
                                </div>
                                <div className="marketing-mockup__detail">
                                    <div className="marketing-mockup__detail-name">bills.lineItems.costRefs</div>
                                    <div className="marketing-mockup__field">
                                        <span className="marketing-mockup__field-name">domainName</span>
                                        <span className="marketing-mockup__field-type"> : string</span>
                                        <span className="marketing-mockup__tag marketing-mockup__tag--required">required</span>
                                    </div>
                                    <div className="marketing-mockup__field">
                                        <span className="marketing-mockup__field-name">orderReleaseGid</span>
                                        <span className="marketing-mockup__field-type"> : string</span>
                                        <span className="marketing-mockup__tag marketing-mockup__tag--required">required</span>
                                    </div>
                                    <div className="marketing-mockup__field">
                                        <span className="marketing-mockup__field-name">insertDate</span>
                                        <span className="marketing-mockup__field-type"> : object → </span>
                                        <span className="marketing-mockup__ref">$ref dateType</span>
                                    </div>
                                    <div className="marketing-mockup__field">
                                        <span className="marketing-mockup__field-name">links</span>
                                        <span className="marketing-mockup__field-type"> : array of links</span>
                                        <span className="marketing-mockup__tag">read-only</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="marketing-section">
                <div className="marketing-section__inner marketing-section__inner--narrow">
                    <div className="marketing-quote">
                        <span className="marketing-pill">Customer reference</span>
                        <blockquote>
                            &ldquo;Our domain experts could finally author the spec themselves. The cross-workgroup{' '}
                            <span className="marketing-mono marketing-text-accent">$ref</span> resolution alone saved us days
                            per release cycle.&rdquo;
                        </blockquote>
                        <p className="marketing-quote__attrib">
                            BiTA Standards Council ran their entire OpenAPI governance on Acuwize Tenet for over a year.
                            The consortium has since wound down operations — but the workflow they validated is the workflow
                            this product still ships.
                        </p>
                    </div>
                </div>
            </section>

            <section id="contact" className="marketing-pricing">
                <div className="marketing-pricing__inner">
                    <h2>Start free. Scale to your consortium.</h2>
                    <div className="marketing-pricing__tiers">
                        <div className="marketing-pricing__tier">
                            <div className="marketing-pricing__tier-label">Free</div>
                            <p>
                                Sign in with Google and try the visual editor, federated{' '}
                                <span className="marketing-mono">$ref</span> resolution, MDX-rich descriptions, and the
                                AsyncAPI bridge on shared SaaS. Capped on schemas, properties, and cross-workgroup refs —
                                enough to evaluate the product, not enough to govern a real standards body on.
                            </p>
                            <Link to="/login" className="marketing-pricing__cta marketing-pricing__cta--primary">
                                Start free
                            </Link>
                        </div>
                        <div className="marketing-pricing__tier">
                            <div className="marketing-pricing__tier-label">Consortium</div>
                            <p>
                                Dedicated Kubernetes deployment under your own subdomain — private repos, SSO setup
                                (Google, Microsoft, SAML, Okta), member onboarding, and ongoing support.
                                Most consortia land in the <strong>$30K–$60K/year</strong> range.
                            </p>
                            <a
                                href="mailto:info@acuwize.com?subject=Acuwize%20Tenet%20%E2%80%94%20enterprise%20enquiry"
                                className="marketing-pricing__cta marketing-pricing__cta--ghost"
                            >
                                Talk to us about enterprise
                            </a>
                        </div>
                    </div>
                    <p className="marketing-pricing__sub">
                        Demos are free, 30 minutes, on a real instance with real data. We&rsquo;ll answer the questions
                        your generic-API-tooling vendor can&rsquo;t.
                    </p>
                </div>
            </section>

            <section id="faq" className="marketing-section">
                <div className="marketing-section__inner marketing-section__inner--narrow">
                    <span className="marketing-pill">Frequently asked</span>
                    <h2 className="marketing-section__title">Honest answers to the usual questions.</h2>

                    <div className="marketing-faq">
                        {[
                            {
                                q: 'How is this different from SwaggerHub or Stoplight?',
                                a: <>SwaggerHub and Stoplight are tuned for one product team shipping one API. Acuwize Tenet is tuned for many editors across many organisations governing a federated bundle of cross-referenced contracts. If you have one team and one repo, use Stoplight — it&rsquo;s a great tool. If you have a foundation workgroup whose changes ripple across seven downstream specs managed by different teams, that&rsquo;s the problem we solve.</>,
                            },
                            {
                                q: 'If LLMs can generate an OpenAPI spec from a prose description, why does anyone need this?',
                                a: <>Authoring a spec is maybe 10% of a standards body&rsquo;s work. The other 90% is review, negotiation, version coordination, comment threading, member buy-in, and impact analysis. LLMs help with the 10%. They don&rsquo;t help with the 90% — and when 14 member companies depend on a schema, &ldquo;the LLM wrote it&rdquo; isn&rsquo;t a defensible answer to &ldquo;why is this field optional?&rdquo; Curation has to be deliberate, by named editors, defending choices in front of peers. Acuwize Tenet is where that happens.</>,
                            },
                            {
                                q: 'Can it handle our spec? We have hundreds of endpoints.',
                                a: <>Yes. We run the full Oracle Transportation Management public API in our demo — 1,168 endpoints, 592 schemas — and it stays responsive. We&rsquo;ve also imported Stripe and Adyen 3.1 as test loads. If your spec runs on GitHub today, it loads in Acuwize Tenet.</>,
                            },
                            {
                                q: 'Where does the canonical published spec live?',
                                a: <>In a GitHub repository. Free-tier specs land in a public repo under your account; enterprise consortia point Acuwize Tenet at a private repo under their organisation. Either way, every workspace is a branch, every published version is a tagged release, and the canonical OpenAPI YAML + Markdown documentation are committed automatically. Member companies subscribe to releases the way they would for any open-source spec.</>,
                            },
                            {
                                q: 'What about AsyncAPI / GraphQL / gRPC?',
                                a: <>REST + webhook events with shared schemas across OpenAPI and AsyncAPI documents work today — the editor handles cross-format <span className="marketing-mono">$ref</span>. Native AsyncAPI authoring (full first-class support beyond webhook bridging) is on the roadmap. GraphQL and gRPC are out of scope.</>,
                            },
                            {
                                q: 'Where is it hosted?',
                                a: <>Two shapes. The free tier runs on Acuwize&rsquo;s multi-tenant SaaS at <span className="marketing-mono">tenetai.acuwize.cloud</span> — same product, shared infrastructure, public specs only, fixed limits. Enterprise consortia get a dedicated Kubernetes deployment under their own subdomain: your data, your access controls, your release cadence, your private repos. Acuwize operates the cluster; you control the standard.</>,
                            },
                            {
                                q: 'Is there a self-serve trial?',
                                a: <>Yes. <Link to="/login">Sign in with Google</Link> on the free tier — no credit card, no demo gate. You get the same visual editor, the same federated <span className="marketing-mono">$ref</span> resolution, and a working git-backed sandbox at <span className="marketing-mono">tenetai.acuwize.cloud</span>. The free tier caps how many schemas, properties, and cross-workgroup refs you can have, plus runs on shared infrastructure with public specs only — enough to kick the tires on real OpenAPI work, not enough to run a standards body. For SSO, private repos, member onboarding, and dedicated Kubernetes, that&rsquo;s the consortium tier; email <a href="mailto:info@acuwize.com">info@acuwize.com</a>.</>,
                            },
                        ].map((item, i) => (
                            <div key={i} className="marketing-faq__item">
                                <h3>{item.q}</h3>
                                <p>{item.a}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="marketing-section marketing-section--tinted">
                <div className="marketing-section__inner marketing-section__inner--narrow marketing-section__inner--center">
                    <h2 className="marketing-section__title">Ready to see it on a real spec?</h2>
                    <p className="marketing-section__lede">
                        Bring your worst, ugliest, biggest OpenAPI document. We&rsquo;ll load it live during the call.
                    </p>
                    <a
                        href="mailto:info@acuwize.com?subject=Acuwize%20Tenet%20%E2%80%94%20demo%20request"
                        className="marketing-btn marketing-btn--primary marketing-btn--lg"
                    >
                        Book a 30-minute demo
                    </a>
                </div>
            </section>

            <footer className="marketing-footer">
                <div className="marketing-footer__inner">
                    <div className="marketing-footer__brand">
                        <div className="marketing-footer__brand-row">
                            <img src={wordmarkUrl} alt="Acuwize" className="marketing-nav__wordmark" />
                            <span className="marketing-nav__sep" aria-hidden="true">/</span>
                            <span className="marketing-nav__product">Tenet</span>
                        </div>
                        <p>
                            Acuwize builds AI-powered platforms for standards bodies and cross-border trade.
                            Acuwize Tenet is our contract layer for industry standards consortia.
                        </p>
                    </div>
                    <div>
                        <div className="marketing-footer__heading">Product</div>
                        <ul>
                            <li><a href="#why">Why Tenet</a></li>
                            <li><a href="#how">How it works</a></li>
                            <li><a href="#capabilities">Capabilities</a></li>
                            <li><a href="#scale">Scale</a></li>
                            <li><a href="#faq">FAQ</a></li>
                        </ul>
                    </div>
                    <div>
                        <div className="marketing-footer__heading">Acuwize</div>
                        <ul>
                            <li><a href="https://acuwize.com">acuwize.com</a></li>
                            <li><a href="mailto:info@acuwize.com">info@acuwize.com</a></li>
                            <li className="marketing-footer__muted">Jodhpur, India</li>
                        </ul>
                    </div>
                </div>
                <div className="marketing-footer__bottom">
                    <div>© {new Date().getFullYear()} Acuwize. Acuwize Tenet and GlobeTrade are products of Acuwize.</div>
                    <div className="marketing-footer__legal">
                        <a href="/privacy">Privacy</a>
                        <a href="/terms">Terms</a>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default Marketing;
